import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { en, type TranslationKey } from "./en";
import {
  localeTag,
  readStoredLocale,
  translateWithResources,
  writeStoredLocale,
  type Locale,
  type TranslationParams,
} from "./core";

export type { Locale } from "./core";

interface I18nValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: TranslationKey, params?: TranslationParams) => string;
  formatNumber: (value: number) => string;
  formatDateTime: (value: string | number | Date) => string;
  formatTime: (value: string | number | Date) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

function initialLocale(): Locale {
  try {
    return readStoredLocale(localStorage);
  } catch {
    return "en";
  }
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      writeStoredLocale(localStorage, next);
    } catch {
      // Storage can be unavailable in privacy-restricted browsers.
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const t = useCallback(
    (key: TranslationKey, params?: TranslationParams) => translateWithResources(locale, key, params),
    [locale],
  );

  const value = useMemo<I18nValue>(() => {
    const languageTag = localeTag(locale);
    return {
      locale,
      setLocale,
      t,
      formatNumber: (number) => number.toLocaleString(languageTag),
      formatDateTime: (input) => new Date(input).toLocaleString(languageTag),
      formatTime: (input) => new Date(input).toLocaleTimeString(languageTag),
    };
  }, [locale, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error("useI18n must be used inside I18nProvider");
  return value;
}
